import { Box, FormField, InputSelectField, Row, Typography } from '@tempmee/react';
import { forwardRef, memo, type Ref, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAverageRate, useRateFloor } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';

import { colors } from '@tempmee/tailwind';
import { useDefaultRecommendedRate } from '../hooks/useDefaultRecommendedRate';
import { SolidCheckCircleIcon, SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';

interface IProfessionalPayFieldProps {
  value?: number | null;
  onChange?: (value: number) => void;
  professionType: string;
  officeId: string;
  hasError?: boolean;
}

const getRateStatus = (rate: number, averageRate: number) => {
  const percentage = (rate / averageRate) * 100;

  if (percentage >= 106) {
    return {
      status: 'More likely to fill.',
      message: 'Rate is competitive with the industry average',
      color: colors.success[500],
      icon: <SolidCheckCircleIcon size={20} color={colors.success[500]} />,
    };
  }
  if (percentage >= 100) {
    return {
      status: 'Likely to fill.',
      message: 'Based on statewide compensation data.',
      color: colors.success[500],
      icon: <SolidCheckCircleIcon size={20} color={colors.success[500]} />,
    };
  }
  if (percentage >= 85) {
    return {
      status: 'Less likely to fill.',
      message: 'Rate is below industry average',
      color: colors.warning[700],
      icon: <SolidExclamationWarningAlertIcon size={20} color={colors.warning[500]} />,
    };
  }
  return {
    status: 'Unlikely to fill.',
    message: 'Rate is well below industry average',
    color: colors.error[500],
    icon: <SolidExclamationWarningAlertIcon size={20} color={colors.error[500]} />,
  };
};

const decorateDropdown = (
  listbox: HTMLElement,
  options: { selectedLabel: string; averageLabel: string },
) => {
  const items = Array.from(listbox.querySelectorAll('[role="option"]')) as HTMLElement[];
  if (items.length === 0) return false;

  items.forEach((item) => {
    const text = item.textContent?.trim() ?? '';
    if (text.includes(' - Average')) {
      item.style.fontWeight = '700';
    }
  });

  const target =
    items.find((item) => item.textContent?.trim() === options.selectedLabel) ??
    items.find((item) => item.textContent?.trim() === options.averageLabel);
  if (target) {
    // Direct scrollTop instead of scrollIntoView: the DS InputSelectField's
    // post-mount focus effect resets the listbox scroll, so we need a value we
    // can re-apply across frames to win that race.
    const desired = target.offsetTop - (listbox.clientHeight - target.offsetHeight) / 2;
    listbox.scrollTop = Math.max(0, desired);
  }
  return true;
};

const Component = (
  { value, onChange, professionType, officeId, hasError }: IProfessionalPayFieldProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { data: averageRating } = useAverageRate(officeId);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const recommendation = professionTypeCode ? averageRating?.[professionTypeCode] : undefined;
  const profRate = recommendation || {
    average: 49,
    min: 18,
    max: 100,
  };

  const averageRate = Math.round(profRate.average);

  const { effectiveMin } = useRateFloor({
    professionCode: professionTypeCode,
    averageRate: profRate.average,
    apiMin: profRate.min,
  });

  // Default the rate to the office's recommended (average) rate. Only pass a
  // value when we have a real recommendation (not the hardcoded fallback).
  useDefaultRecommendedRate({
    value,
    officeId,
    recommendedRate: recommendation ? averageRate : null,
    onChange,
  });

  const rateOptions = useMemo(() => {
    const result = [];
    for (let i = effectiveMin; i <= profRate.max; i++) {
      const isAverage = i === averageRate;
      result.push({
        label: isAverage ? `$${i}.00 /hour - Average` : `$${i}.00 /hour`,
        value: i,
      });
    }
    return result;
  }, [effectiveMin, profRate.max, averageRate]);

  // Keep the selected rate at or above the floor: when an already-saved shift
  // carries a below-floor rate, raise it so it can never be submitted below.
  useEffect(() => {
    if (value != null && value < effectiveMin) {
      onChange?.(effectiveMin);
    }
  }, [effectiveMin, value, onChange]);

  const rateStatus = value != null ? getRateStatus(value, averageRate) : null;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const averageRateRef = useRef(averageRate);
  averageRateRef.current = averageRate;

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleTriggerOpen = () => {
      let attempts = 0;
      let postSuccessFrames = 0;
      const maxAttempts = 30;
      // Keep reapplying scrollTop for a few frames after the first successful
      // decoration so we outlast the DS's open transition (~150ms) and Floating
      // UI's post-mount focus effect, both of which can reset scrollTop to 0.
      const requiredPostSuccessFrames = 5;

      const tryDecorate = () => {
        if (attempts >= maxAttempts) return;
        attempts += 1;

        const expandedTrigger = container.querySelector('[aria-expanded="true"]');
        if (!expandedTrigger) {
          requestAnimationFrame(tryDecorate);
          return;
        }

        const listbox =
          (document.querySelector('[role="listbox"][data-state="open"]') as HTMLElement | null) ??
          (document.querySelector('[role="listbox"]') as HTMLElement | null);
        if (!listbox) {
          requestAnimationFrame(tryDecorate);
          return;
        }

        const selectedRate = valueRef.current;
        const averageLabel = `$${averageRateRef.current}.00 /hour - Average`;
        const selectedLabel =
          selectedRate == null
            ? averageLabel
            : selectedRate === averageRateRef.current
              ? averageLabel
              : `$${selectedRate}.00 /hour`;

        const decorated = decorateDropdown(listbox, { selectedLabel, averageLabel });
        if (decorated) {
          postSuccessFrames += 1;
        }
        if (postSuccessFrames < requiredPostSuccessFrames) {
          requestAnimationFrame(tryDecorate);
        }
      };

      requestAnimationFrame(tryDecorate);
    };

    container.addEventListener('click', handleTriggerOpen);
    container.addEventListener('keydown', handleTriggerOpen);

    return () => {
      container.removeEventListener('click', handleTriggerOpen);
      container.removeEventListener('keydown', handleTriggerOpen);
    };
  }, []);

  return (
    <Box
      ref={setContainerRef}
      data-testid="professional-pay-field"
      data-cy="professional-pay-field"
    >
      <FormField label="Professional Pay">
        <InputSelectField<number>
          options={rateOptions}
          value={value ?? undefined}
          onChange={onChange}
          placeholder="$--.-- /hour"
          isError={Boolean(hasError)}
          disabled={!officeId}
        />
      </FormField>

      {rateStatus && (
        <Row
          align="start"
          gap={2}
          data-testid="rate-status-message"
          data-cy="rate-status-message"
          style={{ marginTop: 12, paddingLeft: 20 }}
        >
          <Box
            as="span"
            role="img"
            aria-label="rate-status"
            data-testid="rate-status-icon"
            data-cy="rate-status-icon"
            style={{ display: 'flex', flexShrink: 0 }}
          >
            {rateStatus.icon}
          </Box>
          <Typography variant="body1" color={colors.neutral[500]}>
            <Typography variant="body1" as="span" bold style={{ color: rateStatus.color }}>
              {rateStatus.status}
            </Typography>{' '}
            {rateStatus.message}
          </Typography>
        </Row>
      )}
    </Box>
  );
};

export const ProfessionalPayField = memo(forwardRef(Component));
